import { defineStore } from 'pinia';
import { useToast } from 'primevue/usetoast';
import type { AppToastMessage } from '@/models/toast';

/**
 * Toasts.
 *
 * This used to hold an array plus a `watch` with a `JSON.stringify` comparison that forwarded the last
 * entry to PrimeVue. Two consequences: the array never drained, so it grew for the whole session; and
 * two identical consecutive toasts produced one — so retrying a save and failing the same way showed
 * nothing at all. Calling `toast.add` directly is equivalent and fixes both.
 */

/** A burst of identical toasts is noise, not information. */
const DEDUPE_WINDOW_MS = 4000;
/** Approximate: PrimeVue does not tell us when a toast closes, so this is a sliding-window rate cap. */
const ERROR_BURST_WINDOW_MS = 10000;
const MAX_ERRORS_PER_WINDOW = 3;

export const useAppStore = defineStore('app', () => {
  /**
   * `useToast()` resolves an injected service and *throws* when ToastService is not installed yet.
   * Letting that escape would take the whole store down with it, and then `addToast` could never report
   * the missing toast layer: the error store would catch an exception from `useAppStore()` rather than
   * read `false`, and every later caller would hit the same failure again.
   */
  const resolveToast = (): ReturnType<typeof useToast> | undefined => {
    try {
      return useToast();
    } catch {
      return undefined;
    }
  };

  // Eager, because this is the one place it can resolve: Pinia runs a setup store inside the app
  // context, so `inject()` works here. A later `addToast` from an interceptor or a timer has no active
  // instance, so resolving *only* lazily would never succeed and would silence every toast.
  let toast = resolveToast();

  const lastShownAt = new Map<string, number>();
  let errorTimestamps: number[] = [];

  /**
   * Copy alone is not identity for an error toast.
   *
   * The taxonomy deliberately gives whole classes of failure the same sentence, so two unrelated
   * incidents seconds apart can read identically while carrying different references — and the
   * reference is the only way the user can report *that* one. Collapsing them hid the second incident
   * and its report button. A retry storm is still collapsed upstream by `shouldPresent`, which keys on
   * the error's fingerprint rather than its wording, so nothing is lost by being strict here.
   */
  const keyOf = (message: AppToastMessage): string =>
    `${message.severity}|${message.summary}|${message.detail}|${String(message.data?.reference ?? '')}`;

  /**
   * Returns whether the toast was actually shown.
   *
   * A boolean rather than a throw: the error store needs to know when there is no toast layer yet so
   * it can fall back to the error screen, but `setSuccess` and the other direct callers must not have
   * an exception introduced into a path that never had one.
   */
  const addToast = (toastModel: AppToastMessage): boolean => {
    // Retried while unresolved rather than left undefined for the session: if this store happened to be
    // created before ToastService was installed, a later call made from inside a component setup can
    // still find it. That is the limit of what `inject()` allows, not a guarantee — hence the boolean.
    toast = toast ?? resolveToast();
    if (!toast) {
      // No ToastService context — an error arriving before App.vue mounted.
      return false;
    }

    const now = Date.now();
    const key = keyOf(toastModel);

    // Pruned first: dedupe only ever looks back `DEDUPE_WINDOW_MS`, so anything older is dead weight
    // and would otherwise accumulate one entry per distinct toast for the life of the tab.
    lastShownAt.forEach((shownAt, shownKey) => {
      if (now - shownAt >= DEDUPE_WINDOW_MS) {
        lastShownAt.delete(shownKey);
      }
    });

    const previous = lastShownAt.get(key);
    if (previous != null && now - previous < DEDUPE_WINDOW_MS) {
      // Suppressed as a duplicate, but the toast layer is working — not a failure.
      return true;
    }
    lastShownAt.set(key, now);

    if (toastModel.severity === 'error') {
      errorTimestamps = errorTimestamps.filter(at => now - at < ERROR_BURST_WINDOW_MS);
      if (errorTimestamps.length >= MAX_ERRORS_PER_WINDOW) {
        return true;
      }
      errorTimestamps.push(now);
    }

    try {
      toast.add(toastModel);
      return true;
    } catch {
      // The service resolved but would not take the message; the caller still needs to know.
      return false;
    }
  };

  return {
    addToast
  };
});
